/**
 * CORE-BL — Guided Onboarding & Product Education
 * First-use delivery: the one automatic invitation.
 *
 * WHY THIS IS NOT IN DASHBOARD ANY MORE
 * -------------------------------------
 * Deciding what to do with the first-use invitation lived in `Dashboard.tsx`,
 * which meant it was delivered only to people whose first landing was the
 * dashboard — and `/` is route-guarded to OWNER, MANAGER, SERVICE_WRITER and
 * TECHNICIAN. For a dispatcher, parts person, auditor, org admin, region or
 * district manager, the component holding their invitation never mounted.
 *
 * It now lives on an onboarding-owned surface mounted globally
 * (`GlobalTutorialBar`), so it runs wherever a person actually lands. Route
 * authority in `App.tsx` is untouched — this is CORE-BL putting its own
 * delivery somewhere it can be reached, not CORE-BL widening access.
 *
 * WHAT IT DECIDES
 * ---------------
 * One thing: whether the invitation was spent. It contains no role switch, no
 * membership read and no track selection — `TutorialContext` resolved all of
 * that, and a second copy here is how the two would come to disagree.
 *
 * The invitation is spent only on a conclusive outcome, and when the outcome
 * is "you cannot have one", the person is TOLD before it is spent. Silently
 * consuming an invitation and rendering nothing is indistinguishable from the
 * feature being broken.
 *
 * IDEMPOTENT PER INVITATION, NOT PER MOUNT
 * ----------------------------------------
 * The guards below are keyed to the invitation's identity. They used to be
 * mount-lifetime booleans, which was survivable when this lived on a page that
 * remounted; mounted globally for a whole session, one settled invitation
 * would have silenced every invitation issued afterwards — accept a second
 * shop's invite in the same tab and nothing would ever happen again.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useTutorial } from '@/contexts/TutorialContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import {
  consumeFirstUse,
  consumesFlag,
  invitationAppliesTo,
  invitationKey,
  readFirstUse,
} from '@/lib/onboarding/firstUseFlag';

/** Delay before starting, so the page the person landed on has settled. */
const START_DELAY_MS = 1000;

export const FirstUseOnboardingCoordinator = () => {
  const {
    flow,
    isLoading,
    experienceResolution,
    progressRead,
    startResolvedTutorial,
  } = useTutorial();
  const { activeShop } = useAuth();

  /**
   * The pending invitation, re-read each render.
   *
   * `localStorage` is not reactive, so this is a read rather than a
   * subscription: the effect below runs whenever the tutorial's own state
   * settles, which is when a decision could be made anyway.
   */
  const invitation = readFirstUse();
  const key = invitationKey(invitation);
  const appliesHere = invitationAppliesTo(invitation, activeShop?.id);

  /** Guards, keyed to the invitation they were set for. */
  const settledForRef = useRef<string | null>(null);
  const attemptedForRef = useRef<string | null>(null);
  const inFlightForRef = useRef<string | null>(null);

  const consume = useCallback(() => {
    settledForRef.current = key;
    consumeFirstUse(activeShop?.id);
  }, [key, activeShop?.id]);

  /** Say why there is nothing, once, in the resolver's own words. */
  const explain = useCallback((message: string | undefined) => {
    toast({
      title: 'No walkthrough for your role yet',
      description:
        message ??
        'There is no guided walkthrough available for your role in this shop.',
    });
  }, []);

  useEffect(() => {
    if (!key || !appliesHere) return;
    if (settledForRef.current === key) return;
    if (inFlightForRef.current === key) return;

    // Still establishing who this is. Not an answer — wait.
    if (isLoading) return;
    if (!experienceResolution) return;

    // The person already has progress in this shop. Their first use has
    // happened; starting would reset it to step 0. Only when that progress was
    // READ, not when this coordinator just put it there.
    if (flow && attemptedForRef.current !== key) {
      consume();
      return;
    }
    if (attemptedForRef.current === key) return;

    // The resolver could not conclude. Its unavailable reasons are reported by
    // `startResolvedTutorial`, which is the authority for them.
    if (experienceResolution.status !== 'experience') return;

    // Do not write over progress that has not been read.
    if (progressRead !== 'loaded') return;

    let cancelled = false;
    inFlightForRef.current = key;
    const timer = setTimeout(() => {
      attemptedForRef.current = key;
      void (async () => {
        const result = await startResolvedTutorial();
        if (cancelled) {
          inFlightForRef.current = null;
          return;
        }
        if (result.status === 'started') {
          consume();
        } else if (consumesFlag(result.reason)) {
          // Terminal — and the person hears about it before the invitation is
          // spent. `route_blocked` and `no_content` are different facts and
          // the resolver words them differently; that wording is carried
          // through rather than rewritten, so nobody is told "nothing is
          // written for your role" when the lessons exist and it is the doors
          // that are shut.
          explain(result.message);
          consume();
        }
        inFlightForRef.current = null;
      })();
    }, START_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      inFlightForRef.current = null;
    };
  }, [
    key,
    appliesHere,
    flow,
    isLoading,
    experienceResolution,
    progressRead,
    startResolvedTutorial,
    consume,
    explain,
  ]);

  return null;
};
